import { ReceiptHeader, ReceiptLine } from '../../common/types';

// Creates a unique grouping key for a receipt line.
// Lines with the same key are considered the same item and will be grouped together.
const makeGroupKey = (l: ReceiptLine) =>
  [
    l.status,
    // For ASN receipts, multiple child lines can share the same original PO line.
    // PO receipts usually have unique child IDs, so grouping would not apply.
    l.originalPoLineId,
    l.partId,
    l.sourcePartId,
    l.purchaseType,
    l.description,
    l.receivingWarehouseId,
    l.receivingWarehouseDefaultLocationId,
    l.inTransitWarehouseId,
    l.inTransitWarehouseLocationId
  ].join('|');

// Generates summary rows with sub-lines for identical receipt items
export const sameItemsSummaryRowGenerator = (poData: ReceiptHeader | null): ReceiptHeader | null => {
  // Return early if no receipt data is provided
  if (!poData) return null;

  // Counter used to generate unique summary IDs
  let summaryIdCounter = 1;

  // Map of grouped lines keyed by the generated group key
  const groups = new Map<
    string,
    {
      summaryId: number;
      children: ReceiptLine[];
    }
  >();

  // Maintains the insertion order of groups
  const groupOrder: string[] = [];

  // First pass – group receipt lines by their group key
  for (const line of poData.lines) {
    const key = makeGroupKey(line);

    // Create a new group if it does not already exist
    if (!groups.has(key)) {
      groups.set(key, { summaryId: summaryIdCounter++, children: [] });
      groupOrder.push(key);
    }

    // Add a cloned copy of the line to the group
    groups.get(key)!.children.push({ ...line });
  }

  // Second pass – build the final list of receipt lines
  const lines: ReceiptLine[] = [];

  for (const key of groupOrder) {
    const { summaryId, children } = groups.get(key)!;

    // If there are multiple identical lines, create a summary row
    if (children.length > 1) {
      // Sum the ordered quantity across all child lines
      const totalOrdered = children.reduce((sum, c) => sum + Number(c.quantityOrdered ?? 0), 0);

      // Use the first child as the template for the summary line
      const firstChild = children[0];

      // Determine ASN line number range for display
      const firstAsnLineNum = children[0].asnLineNum;
      const lastAsnLineNum = children[children.length - 1].asnLineNum;

      // Create the summary line
      const summaryLine: ReceiptLine = {
        ...firstChild,
        id: summaryId,
        subLine: false, // Indicates this is a summary row
        summaryId,
        quantityOrdered: totalOrdered,
        quantityReceived: 0,
        summaryLineSubLineNumberRange:
          firstAsnLineNum === lastAsnLineNum ? `${firstAsnLineNum}` : `${firstAsnLineNum}-${lastAsnLineNum}`
      };

      // Add summary line first
      lines.push(summaryLine);

      // Add child lines as sub-lines under the summary
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        child.subLine = true;
        child.summaryId = summaryId;

        // Mark the last sub-line for UI or processing purposes
        if (i === children.length - 1) {
          child.lastSubLineOfSummary = true;
        }

        lines.push(child);
      }
    } else {
      // If only one line exists, keep it as a normal row
      const solo = { ...children[0] };

      // Remove summary-related properties
      delete (solo as any).summaryId;
      delete (solo as any).subLine;

      lines.push(solo);
    }
  }

  // Return the receipt header with the transformed lines
  return {
    ...poData,
    lines
  };
};

// Helper to determine whether a line is a summary row
export const isSummaryLine = (line: ReceiptLine): boolean => {
  return !line.subLine && Boolean(line.summaryId);
};

// Helper to determine whether there is a summary line
export const thereIsASummaryLine = (lines: ReceiptLine[]): boolean => {
  return lines.some((line) => !line.subLine && Boolean(line.summaryId));
};

// Helper to determine whether a line is a sub-line
export const isSubLine = (line: ReceiptLine): boolean => {
  return Boolean(line.subLine) && Boolean(line.summaryId);
};
